import type { VisualPipelineBlock, VisualPipelineGraph } from "./visual-pipeline-block"

type VisualPipelineNode = VisualPipelineGraph["nodes"][number]
type VisualPipelineEdge = VisualPipelineGraph["edges"][number]
type VisualPipelineNodeType = VisualPipelineBlock["nodeTypes"][number]
type VisualPipelineTemplate = NonNullable<VisualPipelineBlock["templates"]>[number]

export type VisualPipelineTemplateBundle = {
  kind: "workflow.visual-pipeline.template"
  schemaVersion: 1
  template: VisualPipelineTemplate
}

export type VisualPipelineAuthoringMessage = {
  id: string
  tone: "info" | "warning" | "error"
  message: string
  nodeId?: string
  edgeId?: string
}

export type VisualPipelineAuthoringValidation = {
  status: "valid" | "warning" | "error"
  messages: VisualPipelineAuthoringMessage[]
}

export type VisualPipelineTemplateNormalization = {
  templates: VisualPipelineTemplate[]
  messages: VisualPipelineAuthoringMessage[]
}

function text(value: unknown) {
  return typeof value === "string" ? value.trim() : ""
}

function plainRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  return value as Record<string, unknown>
}

function cloneRecord(value: unknown) {
  return structuredClone(plainRecord(value) ?? {})
}

function nextNodeId(graph: VisualPipelineGraph, type: string) {
  const existing = new Set(graph.nodes.map((node) => node.id))
  const base = text(type) || "node"
  const index = Array.from({ length: graph.nodes.length + 2 }, (_, item) => item + 1).find((item) => !existing.has(`${base}-${item}`))
  return `${base}-${index ?? graph.nodes.length + 1}`
}

function nextPosition(graph: VisualPipelineGraph) {
  const maxX = graph.nodes.reduce((value, node) => Math.max(value, node.position.x), 0)
  return { x: graph.nodes.length ? maxX + 260 : 0, y: 0 }
}

function edgeId(from: string, to: string) {
  return `edge-${from}-${to}`
}

function nodeExists(graph: VisualPipelineGraph, nodeId: string) {
  return graph.nodes.some((node) => node.id === nodeId)
}

function templateCandidate(value: unknown): VisualPipelineTemplate | undefined {
  const record = plainRecord(value)
  if (!record || !text(record.id) || !text(record.label) || !isGraph(record.graph)) return undefined
  return {
    id: text(record.id),
    label: text(record.label),
    description: text(record.description) || undefined,
    graph: record.graph,
  }
}

function isGraph(value: unknown): value is VisualPipelineGraph {
  const record = plainRecord(value)
  if (!record || record.schemaVersion !== 1 || !Array.isArray(record.nodes) || !Array.isArray(record.edges)) return false
  return (
    record.nodes.every(
      (node) =>
        Boolean(plainRecord(node)) &&
        typeof (node as { id?: unknown }).id === "string" &&
        typeof (node as { type?: unknown }).type === "string" &&
        typeof (node as { label?: unknown }).label === "string" &&
        Boolean(plainRecord((node as { position?: unknown }).position)) &&
        typeof ((node as { position?: { x?: unknown } }).position?.x) === "number" &&
        typeof ((node as { position?: { y?: unknown } }).position?.y) === "number",
    ) &&
    record.edges.every(
      (edge) =>
        Boolean(plainRecord(edge)) &&
        typeof (edge as { id?: unknown }).id === "string" &&
        typeof (edge as { from?: unknown }).from === "string" &&
        typeof (edge as { to?: unknown }).to === "string",
    )
  )
}

export function addVisualPipelineNode(graph: VisualPipelineGraph, nodeType: VisualPipelineNodeType) {
  const node: VisualPipelineNode = {
    id: nextNodeId(graph, nodeType.type),
    type: nodeType.type,
    label: nodeType.label,
    position: nextPosition(graph),
    data: cloneRecord(nodeType.defaultData),
  }
  return { graph: { ...graph, nodes: [...graph.nodes, node] } }
}

export function removeVisualPipelineNode(graph: VisualPipelineGraph, nodeId: string): VisualPipelineGraph {
  return {
    ...graph,
    nodes: graph.nodes.filter((node) => node.id !== nodeId),
    edges: graph.edges.filter((edge) => edge.from !== nodeId && edge.to !== nodeId),
  }
}

export function addVisualPipelineEdge(graph: VisualPipelineGraph, input: { from: string; to: string }) {
  if (!nodeExists(graph, input.from) || !nodeExists(graph, input.to)) return { graph, error: "Choose a valid source and target." }
  if (input.from === input.to) return { graph, error: "Choose two different nodes." }
  if (graph.edges.some((edge) => edge.from === input.from && edge.to === input.to)) return { graph, error: "This connection already exists." }
  const edge: VisualPipelineEdge = { id: edgeId(input.from, input.to), from: input.from, to: input.to }
  return { graph: { ...graph, edges: [...graph.edges, edge] } }
}

export function removeVisualPipelineEdge(graph: VisualPipelineGraph, edgeId: string): VisualPipelineGraph {
  return { ...graph, edges: graph.edges.filter((edge) => edge.id !== edgeId) }
}

export function validateVisualPipelineAuthoringGraph(
  graph: VisualPipelineGraph,
  nodeTypes: VisualPipelineBlock["nodeTypes"],
): VisualPipelineAuthoringValidation {
  const availableTypes = new Set(nodeTypes.map((nodeType) => nodeType.type))
  const nodeIds = new Set<string>()
  const nodeMessages = graph.nodes.flatMap((node) => {
    const messages: VisualPipelineAuthoringMessage[] = []
    if (nodeIds.has(node.id)) {
      messages.push({ id: `duplicate-node-${node.id}`, tone: "error", message: `Duplicate node id: ${node.id}`, nodeId: node.id })
    }
    nodeIds.add(node.id)
    if (!availableTypes.has(node.type)) {
      messages.push({ id: `unknown-node-type-${node.id}`, tone: "error", message: `Unknown node type: ${node.type}`, nodeId: node.id })
    }
    return messages
  })
  const edgeIds = new Set<string>()
  const connections = new Set<string>()
  const edgeMessages = graph.edges.flatMap((edge) => {
    const connection = `${edge.from}->${edge.to}`
    const messages: VisualPipelineAuthoringMessage[] = []
    if (edgeIds.has(edge.id)) {
      messages.push({ id: `duplicate-edge-${edge.id}`, tone: "error", message: `Duplicate edge id: ${edge.id}`, edgeId: edge.id })
    }
    edgeIds.add(edge.id)
    if (!nodeIds.has(edge.from)) {
      messages.push({ id: `missing-edge-source-${edge.id}`, tone: "error", message: `Edge source node not found: ${edge.from}`, edgeId: edge.id })
    }
    if (!nodeIds.has(edge.to)) {
      messages.push({ id: `missing-edge-target-${edge.id}`, tone: "error", message: `Edge target node not found: ${edge.to}`, edgeId: edge.id })
    }
    if (edge.from === edge.to) {
      messages.push({ id: `self-edge-${edge.id}`, tone: "error", message: "Connection source and target must be different.", edgeId: edge.id })
    }
    if (connections.has(connection)) {
      messages.push({ id: `duplicate-connection-${edge.id}`, tone: "error", message: "Duplicate connections are not allowed.", edgeId: edge.id })
    }
    connections.add(connection)
    return messages
  })
  const messages = [...nodeMessages, ...edgeMessages]
  return {
    status: messages.some((message) => message.tone === "error")
      ? "error"
      : messages.some((message) => message.tone === "warning")
        ? "warning"
        : "valid",
    messages,
  }
}

export function normalizeVisualPipelineTemplates(
  value: unknown,
  nodeTypes: VisualPipelineBlock["nodeTypes"],
): VisualPipelineTemplateNormalization {
  if (!Array.isArray(value)) return { templates: [], messages: [] }
  const entries = value.map((item, index) => ({ item: templateCandidate(item), index }))
  return {
    templates: entries
      .map((entry) => entry.item)
      .filter((item): item is VisualPipelineTemplate => Boolean(item))
      .filter((item) => validateVisualPipelineAuthoringGraph(item.graph, nodeTypes).status !== "error"),
    messages: entries.flatMap((entry) => {
      if (!entry.item) {
        return [{ id: `invalid-template-${entry.index}`, tone: "error", message: `Template at position ${entry.index + 1} is invalid.` }]
      }
      if (validateVisualPipelineAuthoringGraph(entry.item.graph, nodeTypes).status === "error") {
        return [
          {
            id: `invalid-template-${entry.item.id}`,
            tone: "error",
            message: `Template "${entry.item.label}" contains invalid graph data.`,
          },
        ]
      }
      return []
    }),
  }
}

export function exportVisualPipelineTemplateBundle(template: VisualPipelineTemplate): VisualPipelineTemplateBundle {
  return {
    kind: "workflow.visual-pipeline.template",
    schemaVersion: 1,
    template,
  }
}
